import { useAtomValue } from 'jotai'
import React from 'react'
import * as R from 'remeda'
import { match } from 'ts-pattern'

import { useSurveyIdSampleSizeMap } from '~/hooks/useSurveyIdSampleSizeMap'
import {
  ensureExists,
  exists,
  findByPath,
  mapNineSegment,
  type NineNumber,
  nineSegments,
  pathOr,
  sumNineNumbers,
  toNineNumber,
} from '~/utils'

import { tabulationQueryDataAtom } from '../atoms'
import {
  type DataItem,
  indicatorMap,
  isPercentageOnlyIndicator,
} from '../utils'

import { useVisibleBrands } from './useVisibleBrands'
import { useVisibleSurveys } from './useVisibleSurveys'

const indicatorsWithNineSegments = [
  ...nineSegments,
  ...R.pipe(indicatorMap, R.values(), R.flat()),
]

type BrandsDataItem = DataItem & {
  indicator: (typeof indicatorsWithNineSegments)[number]
  brandId: string
  surveyId: string
}

export const useBrandsData = (): BrandsDataItem[] => {
  const tabulationQueryData = useAtomValue(tabulationQueryDataAtom)
  const brands = useVisibleBrands()
  const surveys = useVisibleSurveys()
  const surveyIdSampleSizeMap = useSurveyIdSampleSizeMap()

  return React.useMemo(
    () =>
      brands.flatMap((brand) =>
        surveys.flatMap((survey) => {
          const segmentCounts = R.pipe(
            tabulationQueryData?.brands ?? [],
            findByPath('surveyGroupBrandId', brand.id),
            pathOr('results', []),
            ensureExists,
            findByPath('surveyId', survey.id),
            pathOr('segmentCount', undefined),
          )

          if (!exists(segmentCounts)) {
            return indicatorsWithNineSegments.map<BrandsDataItem>(
              (indicator) => ({
                indicator,
                brandId: brand.id,
                surveyId: survey.id,
                percentage: null,
                estimatedPopulation: null,
              }),
            )
          }

          const nineSegmentCounts = mapNineSegment((nineSegment) =>
            R.pipe(
              segmentCounts,
              findByPath('segmentNumber', toNineNumber(nineSegment)),
              pathOr('count', 0),
            ),
          )
          const sumSegments = (...segmentNumbers: NineNumber[]) =>
            sumNineNumbers(nineSegmentCounts, ...segmentNumbers)

          return indicatorsWithNineSegments.map<BrandsDataItem>((indicator) => {
            const count = match(indicator)
              .with(
                'nineSegment1',
                'nineSegment2',
                'nineSegment3',
                'nineSegment4',
                'nineSegment5',
                'nineSegment6',
                'nineSegment7',
                'nineSegment8',
                'nineSegment9',
                (nineSegment) => nineSegmentCounts[nineSegment],
              )
              .with('rateRecognition', () =>
                sumSegments(8, 7, 6, 5, 4, 3, 2, 1),
              )
              .with('currentUser', () => sumSegments(4, 3, 2, 1))
              .with('loyal', () => sumSegments(2, 1))
              .with('rateCv', () => sumSegments(6, 5, 4, 3, 2, 1))
              .with('ratioLoyalToCurrentUser', () => sumSegments(2, 1))
              .with('npi', () => sumSegments(7, 5, 3, 1))
              .with('aNpi', () => sumSegments(7, 5, 3, 1))
              .with('nNpi', () => sumSegments(7, 5))
              .with('uNpi', () => sumSegments(3, 1))
              .exhaustive()

            const percentage =
              count /
              match(indicator)
                .with('rateCv', () => sumSegments(8, 7, 6, 5, 4, 3, 2, 1))
                .with('ratioLoyalToCurrentUser', () => sumSegments(4, 3, 2, 1))
                .with('aNpi', () => sumSegments(8, 7, 6, 5, 4, 3, 2, 1))
                .with('uNpi', () => sumSegments(4, 3, 2, 1))
                .otherwise(() => surveyIdSampleSizeMap[survey.id] ?? NaN)

            const numberOfTotalAnswers = survey.numberOfTotalAnswers ?? NaN
            const tamNumber = survey.metadata?.tamNumber ?? 0
            const tamRatio =
              tamNumber === 0 ? 1 : tamNumber / numberOfTotalAnswers

            const estimatedPopulation = isPercentageOnlyIndicator(indicator)
              ? null
              : Math.round((count * tamRatio) / 1000)

            return {
              indicator,
              brandId: brand.id,
              surveyId: survey.id,
              percentage,
              estimatedPopulation,
            }
          })
        }),
      ),
    [brands, surveyIdSampleSizeMap, surveys, tabulationQueryData?.brands],
  )
}
